import { API_BASE_URL } from "../config";
import { raiseApiError } from "../lib/api";
import { getAuthHeaders, storeTokens } from "./auth";

export interface TenantInfo {
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
  roles: string[];
  isPersonal: boolean;
  isPrimary: boolean;
}

export interface AvailableTenantsResponse {
  tenants: TenantInfo[];
}

export interface SwitchTenantResponse {
  success: boolean;
  tenantId: string;
  tenantSlug: string;
  tenantName: string;
  roles: string[];
  // Tokens for iOS Safari / cross-origin scenarios
  accessToken?: string;
  refreshToken?: string;
}

export interface TenantMember {
  id: string;
  email: string;
  displayName: string;
  roles: string[];
  isPrimary: boolean;
  joinedAt?: string;
}

export interface TenantMembersResponse {
  members: TenantMember[];
}

export interface InviteMemberRequest {
  email: string;
  roles?: string[];
}

export interface InviteMemberResponse {
  success: boolean;
  userId: string;
  email: string;
  roles: string[];
  joinedAt: string;
}

/**
 * Holt alle verfügbaren Tenants des aktuellen Users
 */
export async function fetchAvailableTenants(apiBaseUrl: string = API_BASE_URL): Promise<TenantInfo[]> {
  const response = await fetch(`${apiBaseUrl}/api/tenants/my`, {
    headers: getAuthHeaders(),
    credentials: "include"
  });

  if (!response.ok) {
    await raiseApiError(response, `Failed to fetch tenants (${response.status})`);
  }

  const data = (await response.json()) as AvailableTenantsResponse;
  return data.tenants;
}

/**
 * Wechselt zum angegebenen Tenant
 */
export async function switchTenant(
  tenantId: string,
  apiBaseUrl: string = API_BASE_URL
): Promise<SwitchTenantResponse> {
  const response = await fetch(`${apiBaseUrl}/api/tenants/switch`, {
    method: "POST",
    headers: {
      ...getAuthHeaders(),
      "Content-Type": "application/json"
    },
    credentials: "include",
    body: JSON.stringify({ tenantId })
  });

  if (!response.ok) {
    await raiseApiError(response, `Failed to switch tenant (${response.status})`);
  }

  const result = await response.json() as SwitchTenantResponse;

  console.log("[Tenant Switch] Response received:", {
    success: result.success,
    tenantSlug: result.tenantSlug,
    hasAccessToken: Boolean(result.accessToken),
    hasRefreshToken: Boolean(result.refreshToken),
    accessTokenLength: result.accessToken?.length,
    refreshTokenLength: result.refreshToken?.length
  });

  // Store tokens for iOS Safari / cross-origin scenarios (same as login)
  if (result.accessToken && result.refreshToken) {
    console.log("[Tenant Switch] Storing new tokens in memory cache + localStorage");
    storeTokens(result.accessToken, result.refreshToken);
  } else {
    console.warn("[Tenant Switch] No tokens in response - relying on cookies only!");
  }

  return result;
}

/**
 * Holt alle Mitglieder eines Tenants
 */
export async function fetchTenantMembers(
  tenantId: string,
  apiBaseUrl: string = API_BASE_URL
): Promise<TenantMember[]> {
  const response = await fetch(`${apiBaseUrl}/api/tenants/${tenantId}/members`, {
    headers: getAuthHeaders(),
    credentials: "include"
  });

  if (!response.ok) {
    await raiseApiError(response, `Failed to fetch members (${response.status})`);
  }

  const data = (await response.json()) as TenantMembersResponse;
  return data.members;
}

/**
 * Lädt einen User zu einem Tenant ein
 */
export async function inviteMember(
  tenantId: string,
  data: InviteMemberRequest,
  apiBaseUrl: string = API_BASE_URL
): Promise<InviteMemberResponse> {
  const response = await fetch(`${apiBaseUrl}/api/tenants/${tenantId}/invitations`, {
    method: "POST",
    headers: {
      ...getAuthHeaders(),
      "Content-Type": "application/json"
    },
    credentials: "include",
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    await raiseApiError(response, `Failed to invite member (${response.status})`);
  }

  return response.json() as Promise<InviteMemberResponse>;
}

/**
 * Entfernt einen User aus einem Tenant
 */
export async function removeMember(
  tenantId: string,
  userId: string,
  apiBaseUrl: string = API_BASE_URL
): Promise<void> {
  const response = await fetch(`${apiBaseUrl}/api/tenants/${tenantId}/members/${userId}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
    credentials: "include"
  });

  if (!response.ok) {
    await raiseApiError(response, `Failed to remove member (${response.status})`);
  }
}
